import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'environments/environment';
import { HttpClient } from '@angular/common/http';
import { RoleDto } from '@bondsports/types';
import { AuthWorker } from '@app/shared/services/auth/AuthWorker';

export const DEFAULT_ORGANIZATION_ID = 0;

/**
 * Fetches grants into AuthWorker for SideNav and landing. Clears the local subject on
 * org-id change so leftover subscribers cannot keep the previous org during the fetch gap.
 * The AuthWorker subscribe unsubscribe is discarded because this is an app-lifetime singleton.
 */
@Injectable()
export class PermissionsService {
	permissions: BehaviorSubject<string[]> = new BehaviorSubject([]);
	private lastOrganizationId: number | undefined;

	constructor(private http: HttpClient) {
		this.lastOrganizationId = AuthWorker.getCurrentOrganization()?.id;
		AuthWorker.subscribeToOrganization(organization => {
			const organizationId = organization?.id;
			if (this.lastOrganizationId === organizationId) {
				return;
			}
			this.lastOrganizationId = organizationId;
			this.permissions.next([]);
		});
	}

	/**
	 * Loads grants for `organizationId`. When `shouldApply` is provided, the response
	 * is discarded unless it still returns true so a superseded `/client` resolve
	 * cannot overwrite the latest org's permissions.
	 */
	async getMyPermissions(organizationId: number, shouldApply?: () => boolean) {
		return await new Promise<void>((resolve, reject) => {
			this.http
				.get<string[]>(`${environment.CS_URLS.API_ROOT_V4}/organization/${organizationId}/users/my/permissions`)
				.subscribe(
					permissions => {
						if (!shouldApply || shouldApply()) {
							this.permissions.next(permissions);
							AuthWorker.setPermissions(permissions, organizationId);
						}
						resolve();
					},
					error => {
						if (!shouldApply || shouldApply()) {
							AuthWorker.setPermissionsError(organizationId);
						}
						reject(error);
					}
				);
		});
	}

	/**
	 * Check if the user has at least one of the required permissions
	 */
	isAllowed(...permissions: string[]): boolean {
		return hasPermissions(AuthWorker.getPermissions(), expandRequiredPermissions(permissions));
	}
}

/**
 * Expands a dotted permission into itself and every parent grant.
 * `pages.calendar.view` also matches `pages.calendar` and `pages`.
 */
export function expandPermissions(permission: string): string[] {
	const parts = permission.split('.');
	return parts.map((_, index) => parts.slice(0, index + 1).join('.'));
}

/**
 * Expands each required grant into itself and every ancestor so page checks match SideNav.
 */
export function expandRequiredPermissions(permissions: string[]): string[] {
	return permissions.flatMap(expandPermissions);
}

export function hasPermissions(permissions: string[] = [], requiredPermissions: string[] = []): boolean {
	if (requiredPermissions.length === 0) {
		return true;
	}
	return requiredPermissions.some(permission => permissions.includes(permission));
}

export function isDefaultRole(role: RoleDto): boolean {
	return role.organizationId === DEFAULT_ORGANIZATION_ID;
}

export function isDefaultModifiedRole(role: RoleDto): boolean {
	return !!role.type && !!role.originalId;
}
